import Loading from "common/components/Elements/Loading/Loading.tsx";
import ErrorPage from "core/components/Screens/ErrorPage/ErrorPage.tsx";
import I18n from "i18n/en-US.ts";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

import { createSequenceAnalyzerSession } from "./api/fetch.ts";
import SequenceAnalyser from "./sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser";

/**
 * Opens the sequence viewer.
 *
 * If the sessionId is not part of the URL it tries to create an empty session.
 * Otherwise, it will open the sequence viewer directly.
 * @constructor
 */
export const SequenceAnalyzer = () => {
  const [searchParams] = useSearchParams();

  const sessionId = searchParams.get("sessionId");
  const navigate = useNavigate();

  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only attempt to create a new session if no sessionId is provided
    if (sessionId === null) {
      setLoading(true);

      createSequenceAnalyzerSession()
        .then((sessionId) => {
          const newParams = new URLSearchParams(searchParams);
          if (sessionId != null) {
            newParams.set("sessionId", sessionId);
            // Update the URL without causing a navigation (replace instead of push)
            navigate(`?${newParams.toString()}`, { replace: true });
          }
        })
        .catch((err: Error) => {
          setError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [sessionId, navigate, searchParams]);

  const sequenceAnalyzerContainer = document.getElementById("sequence-analyzer-module");

  if (!sequenceAnalyzerContainer) {
    return (
      <ErrorPage
        title={I18n.App.ErrorBoundary.title}
        e2eId="containerNotFound"
        errorTitle={"Container element not found"}
        returnToDefaultFirstPage
      />
    );
  }

  // Show error state if session creation failed
  if (error) {
    return (
      <ErrorPage
        title={I18n.App.ErrorBoundary.title}
        e2eId="unhandledError"
        errorTitle={error.message}
        returnToDefaultFirstPage
      />
    );
  }

  // Show loading state while creating session
  if (loading) {
    return <Loading label={I18n.SequenceAnalyser.SequenceAnalyser.creatingSession} />;
  }

  // Only render the SequenceAnalyser component if we have a valid sessionId
  if (sessionId) {
    return <>{createPortal(<SequenceAnalyser />, sequenceAnalyzerContainer)}</>;
  }

  // This is a fallback that should rarely be seen due to the redirect
  return <Loading label={I18n.SequenceAnalyser.SequenceAnalyser.initializingSession} />;
};
